import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { MapPin, Clock, DollarSign, Phone, Globe, Star, Search, Navigation, Building2, ArrowLeft, User, Heart, MessageSquare, Send, Car, Map as MapIcon } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Slider } from "./components/ui/slider";
import OwnerPortal from "./OwnerPortal";
import UserAuth from "./UserAuth";
import AddressInput from "./components/AddressInput";
import GeocodingDemo from "./components/GeocodingDemo";
import MapView from "./components/MapView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLocation, setSearchLocation] = useState("");
  const [coordinates, setCoordinates] = useState(null);
  const [specialTypes, setSpecialTypes] = useState([]);
  const [selectedSpecialType, setSelectedSpecialType] = useState("");
  const [searchRadius, setSearchRadius] = useState(8047); // 5 miles default
  const [priceLevel, setPriceLevel] = useState([1, 4]); // Default: all price levels
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [minRating, setMinRating] = useState(0);
  const [showMapView, setShowMapView] = useState(false);
  const [lastSearch, setLastSearch] = useState(null);
  const [showOwnerPortal, setShowOwnerPortal] = useState(false);
  const [showUserAuth, setShowUserAuth] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userFavorites, setUserFavorites] = useState([]);
  const [showGeocodingDemo, setShowGeocodingDemo] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);

  useEffect(() => {
    fetchSpecialTypes();
    
    // Check if user is already logged in
    const userToken = localStorage.getItem('user_token');
    if (userToken) {
      fetchCurrentUser();
    }
    
    // Load search history from localStorage
    const savedHistory = localStorage.getItem('search_history');
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error('Error loading search history:', error);
      }
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const userToken = localStorage.getItem('user_token');
      if (userToken) {
        const response = await axios.get(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${userToken}` }
        });
        setCurrentUser(response.data);
        fetchUserFavorites();
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      // If token is invalid, clear it
      localStorage.removeItem('user_token');
    }
  };

  const fetchUserFavorites = async () => {
    try {
      const userToken = localStorage.getItem('user_token');
      if (userToken) {
        const response = await axios.get(`${API}/users/favorites`, {
          headers: { Authorization: `Bearer ${userToken}` }
        });
        const favorites = response.data.favorites || [];
        const favoriteIds = favorites.map(fav => fav.id);
        setUserFavorites(favoriteIds);
      }
    } catch (error) {
      console.error('Error fetching user favorites:', error.response?.data || error.message);
    }
  };

  const fetchSpecialTypes = async () => {
    try {
      const response = await axios.get(`${API}/specials/types`);
      setSpecialTypes(response.data.special_types);
    } catch (error) {
      console.error("Error fetching special types:", error);
    }
  };

  const getCurrentLocation = () => {
    setLoading(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCoordinates(coords);
          setSearchLocation("Current Location");
          searchRestaurants(coords.latitude, coords.longitude);
        },
        (error) => {
          console.error("Error getting location:", error);
          setLoading(false);
          alert("Unable to get your location. Please enter a city or address.");
        }
      );
    } else {
      setLoading(false);
      alert("Geolocation is not supported by this browser.");
    }
  };

  const geocodeLocation = async (location) => {
    try {
      // Use backend geocoding endpoint (which uses Google Places API)
      const response = await axios.get(`${API}/geocode`, {
        params: { address: location }
      });
      
      if (response.data.coordinates) {
        return response.data.coordinates;
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }

    // Fallback: try common locations for basic coverage
    const commonLocations = {
      "san francisco": { latitude: 37.7749, longitude: -122.4194 },
      "new york": { latitude: 40.7128, longitude: -74.0060 },
      "los angeles": { latitude: 34.0522, longitude: -118.2437 },
      "chicago": { latitude: 41.8781, longitude: -87.6298 },
      "miami": { latitude: 25.7617, longitude: -80.1918 },
      "new orleans": { latitude: 29.9511, longitude: -90.0715 },
      "boston": { latitude: 42.3601, longitude: -71.0589 },
      "seattle": { latitude: 47.6062, longitude: -122.3321 },
      "denver": { latitude: 39.7392, longitude: -104.9903 },
      "atlanta": { latitude: 33.7490, longitude: -84.3880 }
    };

    const locationKey = location.toLowerCase();
    for (const [key, coords] of Object.entries(commonLocations)) {
      if (locationKey.includes(key)) {
        return coords;
      }
    }

    // Default to San Francisco if location not recognized
    return { latitude: 37.7749, longitude: -122.4194 };
  };

  const handleLocationSearch = async () => {
    if (!searchLocation.trim()) return;
    
    setLoading(true);
    try {
      const coords = await geocodeLocation(searchLocation);
      setCoordinates(coords);
      searchRestaurants(coords.latitude, coords.longitude);
    } catch (error) {
      console.error("Error geocoding location:", error);
      setLoading(false);
    }
  };

  // Enhanced handler for AddressInput component
  const handleAddressSelect = (geocodeResult) => {
    setSearchLocation(geocodeResult.formatted_address);
    setCoordinates({
      latitude: geocodeResult.latitude,
      longitude: geocodeResult.longitude
    });
    setLoading(true);
    searchRestaurants(geocodeResult.latitude, geocodeResult.longitude);
  };

  const searchRestaurants = async (lat, lng) => {
    try {
      const params = {
        latitude: lat,
        longitude: lng,
        radius: searchRadius,
        limit: 20
      };

      if (selectedSpecialType && selectedSpecialType !== "all") {
        params.special_type = selectedSpecialType;
      }

      // Add price level filter if not showing all
      if (priceLevel[0] !== 1 || priceLevel[1] !== 4) {
        params.price_level = priceLevel[1];
      }

      // Add cuisine filter
      if (selectedCuisines.length > 0) {
        params.cuisine_types = selectedCuisines.join(',');
      }

      // Add dietary filter
      if (selectedDietary.length > 0) {
        params.dietary_options = selectedDietary.join(',');
      }

      // Add rating filter
      if (minRating > 0) {
        params.min_rating = minRating;
      }

      const response = await axios.get(`${API}/restaurants/search`, { params });
      setRestaurants(response.data.restaurants);
      setLastSearch(response.data.search_location);
      setLoading(false);

      // Save to search history
      saveSearchHistory(searchLocation, lat, lng);
    } catch (error) {
      console.error("Error searching restaurants:", error);
      setLoading(false);
    }
  };

  const saveSearchHistory = (location, lat, lng) => {
    if (!location) return;

    const newHistory = [
      { location, latitude: lat, longitude: lng, timestamp: Date.now() },
      ...searchHistory.filter(h => h.location !== location)
    ].slice(0, 10); // Keep only last 10 searches

    setSearchHistory(newHistory);
    localStorage.setItem('search_history', JSON.stringify(newHistory));
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDistance = (distanceMeters) => {
    const miles = (distanceMeters * 0.000621371).toFixed(1);
    return `${miles} mi`;
  };

  const generateShareMessage = (restaurant) => {
    let message = `Check out ${restaurant.name}!\n\n`;
    message += `📍 ${restaurant.address}\n\n`;
    
    if (restaurant.specials && restaurant.specials.length > 0) {
      message += `Current Specials:\n`;
      restaurant.specials.forEach((special, index) => {
        message += `${index + 1}. ${special.title} - $${special.price}\n`;
        message += `   ${special.description}\n`;
        message += `   ${formatTime(special.time_start)} - ${formatTime(special.time_end)}\n`;
      });
    }
    
    return encodeURIComponent(message);
  };

  const handleSMSShare = (restaurant) => {
    const message = generateShareMessage(restaurant);
    // Use location.href to avoid blank tab on desktop
    window.location.href = `sms:?body=${message}`;
  };

  const handleWhatsAppShare = (restaurant) => {
    const message = generateShareMessage(restaurant);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleTelegramShare = (restaurant) => {
    const message = generateShareMessage(restaurant);
    const shareUrl = restaurant.website || window.location.origin;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${message}`, '_blank');
  };

  const handleMessengerShare = (restaurant) => {
    const message = generateShareMessage(restaurant);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent);
    const shareUrl = restaurant.website || window.location.href;
    
    if (isMobile) {
      // Use fb-messenger URI scheme for mobile
      window.location.href = `fb-messenger://share/?link=${encodeURIComponent(shareUrl)}&app_id=140586622674265`;
    } else {
      // Fallback to navigator.share or Facebook sharer for desktop
      if (navigator.share) {
        navigator.share({
          title: restaurant.name,
          text: decodeURIComponent(message),
          url: shareUrl
        }).catch((error) => console.log('Share cancelled', error));
      } else {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
      }
    }
  };

  const handleUberShare = async (restaurant) => {
    const dropoffLat = restaurant.location?.latitude;
    const dropoffLng = restaurant.location?.longitude;
    const dropoffAddress = encodeURIComponent(restaurant.address);
    
    // Try to get actual user GPS location
    if ("geolocation" in navigator) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        
        const pickupLat = position.coords.latitude;
        const pickupLng = position.coords.longitude;
        const uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup[latitude]=${pickupLat}&pickup[longitude]=${pickupLng}&dropoff[latitude]=${dropoffLat}&dropoff[longitude]=${dropoffLng}&dropoff[formatted_address]=${dropoffAddress}`;
        window.open(uberUrl, '_blank');
      } catch (error) {
        // Fallback to search coordinates or destination only
        if (coordinates) {
          const uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup[latitude]=${coordinates.latitude}&pickup[longitude]=${coordinates.longitude}&dropoff[latitude]=${dropoffLat}&dropoff[longitude]=${dropoffLng}&dropoff[formatted_address]=${dropoffAddress}`;
          window.open(uberUrl, '_blank');
        } else {
          // Just set destination
          const uberUrl = `https://m.uber.com/ul/?action=setPickup&dropoff[latitude]=${dropoffLat}&dropoff[longitude]=${dropoffLng}&dropoff[formatted_address]=${dropoffAddress}`;
          window.open(uberUrl, '_blank');
        }
      }
    } else {
      // Fallback to destination only
      const uberUrl = `https://m.uber.com/ul/?action=setPickup&dropoff[latitude]=${dropoffLat}&dropoff[longitude]=${dropoffLng}&dropoff[formatted_address]=${dropoffAddress}`;
      window.open(uberUrl, '_blank');
    }
  };

  const handleLyftShare = async (restaurant) => {
    const destLat = restaurant.location?.latitude;
    const destLng = restaurant.location?.longitude;
    
    // Try to get actual user GPS location
    if ("geolocation" in navigator) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        
        const pickupLat = position.coords.latitude;
        const pickupLng = position.coords.longitude;
        const lyftUrl = `https://lyft.com/ride?pickup[latitude]=${pickupLat}&pickup[longitude]=${pickupLng}&destination[latitude]=${destLat}&destination[longitude]=${destLng}`;
        window.open(lyftUrl, '_blank');
      } catch (error) {
        // Fallback to search coordinates or destination only
        if (coordinates) {
          const lyftUrl = `https://lyft.com/ride?pickup[latitude]=${coordinates.latitude}&pickup[longitude]=${coordinates.longitude}&destination[latitude]=${destLat}&destination[longitude]=${destLng}`;
          window.open(lyftUrl, '_blank');
        } else {
          // Just set destination
          const lyftUrl = `https://lyft.com/ride?destination[latitude]=${destLat}&destination[longitude]=${destLng}`;
          window.open(lyftUrl, '_blank');
        }
      }
    } else {
      // Fallback to destination only
      const lyftUrl = `https://lyft.com/ride?destination[latitude]=${destLat}&destination[longitude]=${destLng}`;
      window.open(lyftUrl, '_blank');
    }
  };

  const getSpecialTypeLabel = (type) => {
    const specialType = specialTypes.find(st => st.value === type);
    return specialType ? specialType.label : type;
  };

  const getSpecialTypeBadgeColor = (type) => {
    const colors = {
      happy_hour: "bg-orange-100 text-orange-800 border-orange-200",
      lunch_special: "bg-green-100 text-green-800 border-green-200",
      dinner_special: "bg-purple-100 text-purple-800 border-purple-200",
      blue_plate: "bg-blue-100 text-blue-800 border-blue-200",
      daily_special: "bg-red-100 text-red-800 border-red-200",
      weekend_special: "bg-yellow-100 text-yellow-800 border-yellow-200"
    };
    return colors[type] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const toggleFavorite = async (restaurantId) => {
    if (!currentUser) {
      setShowUserAuth(true);
      return;
    }

    try {
      const userToken = localStorage.getItem('user_token');
      const isFavorite = userFavorites.includes(restaurantId);
      
      if (isFavorite) {
        await axios.delete(`${API}/users/favorites/${restaurantId}`, {
          headers: { Authorization: `Bearer ${userToken}` }
        });
        const newFavorites = userFavorites.filter(id => id !== restaurantId);
        setUserFavorites(newFavorites);
      } else {
        await axios.post(`${API}/users/favorites/${restaurantId}`, {}, {
          headers: { Authorization: `Bearer ${userToken}` }
        });
        const newFavorites = [...userFavorites, restaurantId];
        setUserFavorites(newFavorites);
        
        // Refresh favorites from server to ensure synchronization
        setTimeout(() => {
          fetchUserFavorites();
        }, 500);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error.response?.data || error.message);
    }
  };

  const handleUserLogin = (userData) => {
    setCurrentUser(userData);
    if (userData) {
      fetchUserFavorites();
    } else {
      setUserFavorites([]);
    }
  };

  const handleUserLogout = () => {
    setCurrentUser(null);
    setUserFavorites([]);
    localStorage.removeItem('user_token');
  };

  // If showing owner portal, render it
  if (showOwnerPortal) {
    return <OwnerPortal />;
  }

  // If showing map view, render map
  if (showMapView) {
    return (
      <MapView
        restaurants={restaurants}
        center={coordinates ? { lat: coordinates.latitude, lng: coordinates.longitude } : null}
        onClose={() => setShowMapView(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="text-center flex-1">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                On-the-Cheap
              </h1>
              <p className="text-lg text-gray-600">
                Find the best restaurant specials near you
              </p>
            </div>
            <div className="flex gap-2">
              {currentUser ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowUserAuth(true)}
                    className="border-orange-600 text-orange-600 hover:bg-orange-50"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {currentUser.first_name}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowUserAuth(true)}
                  className="border-orange-600 text-orange-600 hover:bg-orange-50"
                >
                  <User className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowOwnerPortal(true)}
                className="border-orange-600 text-orange-600 hover:bg-orange-50"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Restaurant Owner
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowGeocodingDemo(true)}
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                🌍 Geocoding Demo
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-semibold">Find Restaurant Specials</CardTitle>
            <CardDescription>
              Search by location or use your current position to find amazing deals
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <AddressInput
                  placeholder="Enter city or address (e.g., San Francisco, New York)"
                  initialValue={searchLocation}
                  onAddressSelect={handleAddressSelect}
                  onChange={setSearchLocation}
                  region="US"
                  className="w-full"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleLocationSearch}
                  disabled={!searchLocation.trim() || loading}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                <Button 
                  variant="outline" 
                  onClick={getCurrentLocation}
                  disabled={loading}
                  className="border-orange-600 text-orange-600 hover:bg-orange-50"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Use Current Location
                </Button>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Select value={selectedSpecialType} onValueChange={setSelectedSpecialType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by special type (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specials</SelectItem>
                    {specialTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Select value={searchRadius.toString()} onValueChange={(value) => setSearchRadius(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1609">1 mile</SelectItem>
                    <SelectItem value="3219">2 miles</SelectItem>
                    <SelectItem value="8047">5 miles</SelectItem>
                    <SelectItem value="16094">10 miles</SelectItem>
                    <SelectItem value="32187">20 miles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price Range Filter */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">
                  Max Price Range
                </label>
                <span className="text-sm text-gray-600">
                  {'$'.repeat(priceLevel[1])} {priceLevel[1] === 4 ? '(Most Expensive)' : ''}
                </span>
              </div>
              <Slider
                value={priceLevel}
                onValueChange={setPriceLevel}
                min={1}
                max={4}
                step={1}
                className="w-full"
                data-testid="price-range-slider"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>$ (Cheap)</span>
                <span>$$ (Moderate)</span>
                <span>$$$ (Pricey)</span>
                <span>$$$$ (Fine Dining)</span>
              </div>
            </div>

            {/* Cuisine Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Cuisine Type</label>
              <div className="flex flex-wrap gap-2">
                {['American', 'Italian', 'Mexican', 'Chinese', 'Japanese', 'Thai', 'Indian', 'Mediterranean'].map((cuisine) => (
                  <Badge
                    key={cuisine}
                    variant={selectedCuisines.includes(cuisine.toLowerCase()) ? "default" : "outline"}
                    className={`cursor-pointer ${selectedCuisines.includes(cuisine.toLowerCase()) ? 'bg-orange-600' : ''}`}
                    onClick={() => {
                      const cuisineLower = cuisine.toLowerCase();
                      setSelectedCuisines(prev =>
                        prev.includes(cuisineLower)
                          ? prev.filter(c => c !== cuisineLower)
                          : [...prev, cuisineLower]
                      );
                    }}
                  >
                    {cuisine}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Dietary Options Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Dietary Options</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Vegetarian', value: 'vegetarian' },
                  { label: 'Vegan', value: 'vegan' },
                  { label: 'Gluten-Free', value: 'gluten_free' },
                  { label: 'Halal', value: 'halal' },
                  { label: 'Kosher', value: 'kosher' }
                ].map((option) => (
                  <Badge
                    key={option.value}
                    variant={selectedDietary.includes(option.value) ? "default" : "outline"}
                    className={`cursor-pointer ${selectedDietary.includes(option.value) ? 'bg-green-600' : ''}`}
                    onClick={() => {
                      setSelectedDietary(prev =>
                        prev.includes(option.value)
                          ? prev.filter(d => d !== option.value)
                          : [...prev, option.value]
                      );
                    }}
                  >
                    {option.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Minimum Rating Filter */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">
                  Minimum Rating
                </label>
                <span className="text-sm text-gray-600">
                  {minRating > 0 ? `${minRating}+ ⭐` : 'Any Rating'}
                </span>
              </div>
              <Slider
                value={[minRating]}
                onValueChange={(value) => setMinRating(value[0])}
                min={0}
                max={5}
                step={0.5}
                className="w-full"
                data-testid="min-rating-slider"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Any</span>
                <span>3.0+</span>
                <span>4.0+</span>
                <span>4.5+</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search History */}
        {searchHistory.length > 0 && !loading && restaurants.length === 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Recent Searches</CardTitle>
              <CardDescription>Quick access to your recent locations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {searchHistory.slice(0, 5).map((item, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchLocation(item.location);
                      setCoordinates({ latitude: item.latitude, longitude: item.longitude });
                      setLoading(true);
                      searchRestaurants(item.latitude, item.longitude);
                    }}
                    className="text-sm"
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    {item.location}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            <p className="mt-4 text-gray-600">Finding the best deals near you...</p>
          </div>
        )}

        {/* Results Header */}
        {restaurants.length > 0 && (
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Current Specials Near You
                </h2>
                <p className="text-gray-600">
                  Found {restaurants.length} restaurants with active specials
                  {lastSearch && ` within ${formatDistance(searchRadius)} of your search location`}
                </p>
              </div>
              <Button
                onClick={() => setShowMapView(true)}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="show-map-view"
              >
                <MapIcon className="w-4 h-4 mr-2" />
                Map View
              </Button>
            </div>
          </div>
        )}

        {/* Restaurant Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {restaurants.map((restaurant) => (
            <Card key={restaurant.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-900">
                      {restaurant.name}
                    </CardTitle>
                    <CardDescription className="flex items-center mt-1">
                      <MapPin className="w-4 h-4 mr-1" />
                      {restaurant.distance && formatDistance(restaurant.distance)}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFavorite(restaurant.id)}
                      className={`p-1 rounded-full transition-colors ${
                        userFavorites.includes(restaurant.id)
                          ? 'text-red-500 hover:text-red-600'
                          : 'text-gray-400 hover:text-red-500'
                      }`}
                      title={userFavorites.includes(restaurant.id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart 
                        className={`w-5 h-5 ${
                          userFavorites.includes(restaurant.id) ? 'fill-current' : ''
                        }`} 
                      />
                    </button>
                    {restaurant.rating && (
                      <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                        <span className="text-sm font-medium">{restaurant.rating}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {restaurant.address}
                  </p>
                  
                  {restaurant.phone && (
                    <p className="text-sm text-gray-600 flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {restaurant.phone}
                    </p>
                  )}
                  
                  {restaurant.website && (
                    <p className="text-sm text-gray-600 flex items-center">
                      <Globe className="w-4 h-4 mr-2" />
                      <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">
                        Visit Website
                      </a>
                    </p>
                  )}

                  <div className="flex flex-wrap gap-1 mb-3">
                    {restaurant.cuisine_type?.map((cuisine) => (
                      <Badge key={cuisine} variant="secondary" className="text-xs">
                        {cuisine}
                      </Badge>
                    ))}
                  </div>

                  <div className="border-t pt-3">
                    <h4 className="font-semibold text-gray-900 mb-2">Current Specials</h4>
                    <div className="space-y-3">
                      {restaurant.specials?.length > 0 ? (
                        restaurant.specials.map((special) => (
                          <div key={special.id} className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="font-medium text-gray-900">{special.title}</h5>
                              <Badge className={getSpecialTypeBadgeColor(special.special_type)}>
                                {getSpecialTypeLabel(special.special_type)}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{special.description}</p>
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center text-green-600 font-medium">
                                <DollarSign className="w-4 h-4 mr-1" />
                                ${special.price}
                                {special.original_price && (
                                  <span className="ml-2 text-gray-500 line-through">
                                    ${special.original_price}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center text-gray-600">
                                <Clock className="w-4 h-4 mr-1" />
                                {formatTime(special.time_start)} - {formatTime(special.time_end)}
                              </div>
                            </div>
                            <div className="mt-2">
                              <p className="text-xs text-gray-600">
                                Available: {special.days_available.map(day => 
                                  day.charAt(0).toUpperCase() + day.slice(1)
                                ).join(', ')}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 px-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">
                            {restaurant.specials_message || 'No current specials at this time'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Share and Ride-sharing buttons */}
                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <button
                        onClick={() => handleSMSShare(restaurant)}
                        className="flex flex-col items-center p-2 rounded-lg hover:bg-gray-100 transition-colors group"
                        title="Share via SMS"
                        aria-label="Share via SMS"
                        data-testid={`share-sms-${restaurant.id}`}
                      >
                        <MessageSquare className="w-5 h-5 text-gray-600 group-hover:text-green-600" />
                        <span className="text-xs text-gray-500 mt-1">SMS</span>
                      </button>
                      <button
                        onClick={() => handleWhatsAppShare(restaurant)}
                        className="flex flex-col items-center p-2 rounded-lg hover:bg-gray-100 transition-colors group"
                        title="Share via WhatsApp"
                        aria-label="Share via WhatsApp"
                        data-testid={`share-whatsapp-${restaurant.id}`}
                      >
                        <MessageSquare className="w-5 h-5 text-green-600 group-hover:text-green-700" />
                        <span className="text-xs text-gray-500 mt-1">WhatsApp</span>
                      </button>
                      <button
                        onClick={() => handleTelegramShare(restaurant)}
                        className="flex flex-col items-center p-2 rounded-lg hover:bg-gray-100 transition-colors group"
                        title="Share via Telegram"
                        aria-label="Share via Telegram"
                        data-testid={`share-telegram-${restaurant.id}`}
                      >
                        <Send className="w-5 h-5 text-blue-500 group-hover:text-blue-600" />
                        <span className="text-xs text-gray-500 mt-1">Telegram</span>
                      </button>
                      <button
                        onClick={() => handleMessengerShare(restaurant)}
                        className="flex flex-col items-center p-2 rounded-lg hover:bg-gray-100 transition-colors group"
                        title="Share via Messenger"
                        aria-label="Share via Messenger"
                        data-testid={`share-messenger-${restaurant.id}`}
                      >
                        <MessageSquare className="w-5 h-5 text-blue-600 group-hover:text-blue-700 fill-current" />
                        <span className="text-xs text-gray-500 mt-1">Messenger</span>
                      </button>
                      <div className="w-px h-12 bg-gray-300 mx-1"></div>
                      <button
                        onClick={() => handleUberShare(restaurant)}
                        className="flex flex-col items-center p-2 rounded-lg hover:bg-gray-100 transition-colors group"
                        title="Get Uber"
                        aria-label="Get Uber ride"
                        data-testid={`rideshare-uber-${restaurant.id}`}
                      >
                        <Car className="w-5 h-5 text-black group-hover:text-gray-700" />
                        <span className="text-xs text-gray-500 mt-1">Uber</span>
                      </button>
                      <button
                        onClick={() => handleLyftShare(restaurant)}
                        className="flex flex-col items-center p-2 rounded-lg hover:bg-gray-100 transition-colors group"
                        title="Get Lyft"
                        aria-label="Get Lyft ride"
                        data-testid={`rideshare-lyft-${restaurant.id}`}
                      >
                        <Car className="w-5 h-5 text-pink-500 group-hover:text-pink-600" />
                        <span className="text-xs text-gray-500 mt-1">Lyft</span>
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {!loading && restaurants.length === 0 && coordinates && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4">🍽️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Active Specials Found
              </h3>
              <p className="text-gray-600 mb-4">
                Try expanding your search radius or check back later for new specials.
              </p>
              <Button 
                onClick={() => {
                  setSearchRadius(16094); // 10 miles
                  if (coordinates) {
                    searchRestaurants(coordinates.latitude, coordinates.longitude);
                  }
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Search Wider Area (10 miles)
              </Button>
            </div>
          </div>
        )}

        {/* Welcome Message */}
        {!loading && restaurants.length === 0 && !coordinates && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4">🍻</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Welcome to On-the-Cheap!
              </h3>
              <p className="text-gray-600 mb-4">
                Find amazing restaurant specials, happy hours, and daily deals near you. 
                Enter your location above to get started.
              </p>
              <div className="bg-orange-50 p-4 rounded-lg mt-4">
                <p className="text-sm text-orange-800">
                  💡 <strong>Pro tip:</strong> We show only specials that are currently active based on the time and day!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Authentication Modal */}
      {showUserAuth && (
        <UserAuth 
          onClose={() => setShowUserAuth(false)}
          onUserLogin={handleUserLogin}
          currentFavorites={userFavorites}
          onFavoritesUpdate={setUserFavorites}
        />
      )}

      {/* Geocoding Demo Modal */}
      {showGeocodingDemo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold">Google Geocoding API Demo</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGeocodingDemo(false)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
            <div className="p-4">
              <GeocodingDemo />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">On-the-Cheap</h3>
            <p className="text-gray-600 text-sm">
              Discover the best restaurant specials and save money on great food!
            </p>
            <div className="mt-4 text-xs text-gray-500">
              © 2024 On-the-Cheap. Find deals, save money, eat well.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;